/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoArrowDown } from '../../components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioGroupBoxField } from '../../components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import { hasAnySim } from '../../utils-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta08ForaAlcanceEnum,
  ConsultaSeguimentoPergunta08Model,
} from './model-consultaSeguimentoPergunta08'
import { isPergunta08ForaAlcanceAllNao } from './utils-consultaSeguimentoPergunta08'

interface ConsultaSeguimentoPergunta02FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta08Model>
}

export function ConsultaSeguimentoPergunta08Field(props: Readonly<ConsultaSeguimentoPergunta02FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta08Model>(resolveName(name), {
    subscription: { value: true },
  })

  const notEndAt02 =
    value.PERGUNTA_08_HEADER === SimNaoEnum.NAO ||
    (value.PERGUNTA_08_HEADER === SimNaoEnum.SIM &&
      value.PERGUNTA_08_SE_INTERESSA_POR &&
      value.PERGUNTA_08_SE_INTERESSA_POR !== SimNaoEnum.SIM)

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta08Model>({
    name,
    calculateResultado,
    conditionToCalculate: conditionToCalculate,
  })

  return (
    <VFlow>
      <ConsultaSeguimentoPerguntaHeader
        pergunta={MChatPerguntaEnum.PERGUNTA_08}
        title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' se interessa por outras crianças?' />}
        status={value.status}
        field={<SimNaoSwitchButtonField name={name.PERGUNTA_08_HEADER} />}
      />
      {value.PERGUNTA_08_HEADER && (
        <VFlow vSpacing={0}>
          <ConsultaSeguimentoIndicatorStart />
          {value.PERGUNTA_08_HEADER === SimNaoEnum.SIM && (
            <PerguntaSimNaoRadioGroupBoxField
              name={name.PERGUNTA_08_SE_INTERESSA_POR}
              label='Ele se interessa por crianças que não são irmãos dele?'
              required
            />
          )}
          {notEndAt02 && (
            <VFlow vSpacing={0}>
              {value.PERGUNTA_08_HEADER === SimNaoEnum.SIM && <ConsultaSeguimentoArrowDown />}
              <PerguntaSimNaoRadioGroupBoxField
                name={name.PERGUNTA_08_RESPONDE_PERGUNTAS}
                label='Quando vocês estão na pracinha ou no supermercado, seu filho geralmente responde perguntas de outras crianças?'
                required
              />
              {value.PERGUNTA_08_RESPONDE_PERGUNTAS === SimNaoEnum.SIM && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoArrowDown />
                  <ConsultaSeguimentoIndicatorHelper
                    title='Se seu filho quer algo que está fora do alcance dele, como uma comida ou um brinquedo que estejam fora do alcance dele, o que ele faz para conseguir pegar?'
                    subtitle='(Se o genitor não der nenhum dos exemplos abaixo, perguntar cada um deles individualmente)'
                  />
                  <VFlow vSpacing={0} style={styles.perguntaOlharAlgoInteressante}>
                    <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta08ForaAlcanceEnum>
                      name={name.PERGUNTA_08_FORA_ALCANCE}
                      label='Seu filho...'
                      description='Responda os exemplos até pontuar na Pergunta 8 ou avançar para o próximo fluxo'
                      options={PERGUNTA_08_SEU_FILHO_OPTIONS}
                      required
                    />
                    {hasAnySim(value?.PERGUNTA_08_FORA_ALCANCE) && (
                      <VFlow vSpacing={0}>
                        <ConsultaSeguimentoIndicatorStep text='Sim para qualquer um:' />
                        <PerguntaSimNaoRadioGroupBoxField
                          name={name.PERGUNTA_08_RESPONDE_CRIANCAS}
                          label='Ele responde a outras crianças mais da metade das vezes?'
                          required
                        />
                      </VFlow>
                    )}
                  </VFlow>
                </VFlow>
              )}
            </VFlow>
          )}
          {value.resultado && (
            <ConsultaSeguimentoIndicatorResultado
              text={{
                FALHOU: isPergunta08ForaAlcanceAllNao(value.PERGUNTA_08_FORA_ALCANCE) && 'Não para todos:',
              }}
              resultado={value.resultado}
            />
          )}
        </VFlow>
      )}
    </VFlow>
  )
}

const PERGUNTA_08_SEU_FILHO_OPTIONS: RadioOption<ConsultaSeguimentoPergunta08ForaAlcanceEnum>[] = [
  {
    label: 'Brinca com outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.BRINCA_COM_OUTRA_CRIANCA,
  },
  {
    label: 'Fala com outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.FALA_COM_OUTRA_CRIANCA,
  },
  {
    label: 'Balbucia ou faz barulhos com a boca?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.BALBUCIA_FAZ_BARULHO,
  },
  {
    label: 'Olha para outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.OLHA_PRA_OUTRA_CRIANCA,
  },
  {
    label: 'Sorri para outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.SORRI_PRA_OUTRA_CRIANCA,
  },
  {
    label: 'Primeiro fica tímido, mas depois sorri?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.FICA_TIMIDO,
  },
  {
    label: 'Fica animado com a outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.FICA_ANIMADO,
  },
]

const calculateResultado = (values: ConsultaSeguimentoPergunta08Model) => {
  if (values.PERGUNTA_08_HEADER === SimNaoEnum.SIM && values.PERGUNTA_08_SE_INTERESSA_POR === SimNaoEnum.SIM) {
    return PassouFalhouEnum.PASSOU
  } else if (values.PERGUNTA_08_RESPONDE_PERGUNTAS === SimNaoEnum.NAO) {
    return PassouFalhouEnum.FALHOU
  } else if (isPergunta08ForaAlcanceAllNao(values.PERGUNTA_08_FORA_ALCANCE)) {
    return PassouFalhouEnum.FALHOU
  } else if (values.PERGUNTA_08_RESPONDE_CRIANCAS === SimNaoEnum.SIM) {
    return PassouFalhouEnum.PASSOU
  } else if (values.PERGUNTA_08_RESPONDE_CRIANCAS === SimNaoEnum.NAO) {
    return PassouFalhouEnum.FALHOU
  }
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta08Model): boolean => {
  const isHeaderSimESeInteressaPor =
    values.PERGUNTA_08_HEADER === SimNaoEnum.SIM && values.PERGUNTA_08_SE_INTERESSA_POR === SimNaoEnum.SIM

  if (isHeaderSimESeInteressaPor) {
    return true
  }

  const isHeaderNaoOuHeaderSimENaoSeInteressaPor =
    values.PERGUNTA_08_HEADER === SimNaoEnum.NAO ||
    (values.PERGUNTA_08_HEADER === SimNaoEnum.SIM && values.PERGUNTA_08_SE_INTERESSA_POR === SimNaoEnum.NAO)

  if (!isHeaderNaoOuHeaderSimENaoSeInteressaPor) {
    return false
  }

  const isRespondePerguntasNao = values.PERGUNTA_08_RESPONDE_PERGUNTAS === SimNaoEnum.NAO
  const isForaAlcanceTodosNao = isPergunta08ForaAlcanceAllNao(values.PERGUNTA_08_FORA_ALCANCE)
  const hasRespondeCriancas = !isEmpty(values.PERGUNTA_08_RESPONDE_CRIANCAS)

  return isRespondePerguntasNao || isForaAlcanceTodosNao || hasRespondeCriancas
}

const styles = {
  perguntaOlharAlgoInteressante: css`
    margin-top: 1rem;
  `,
}
